import React, { useState } from "react";
import { Link } from "react-router-dom";

import Panel from "components/Panel";

import {
  formatBytes,
  formatPercent,
  formatNumber,
  formatBytesTrend,
  formatBytesTrendDetail,
} from "utils/format";
import Loading from "components/Loading";

import {
  useSchemaTableListQuery,
  SchemaTableListQuery,
} from "./gql/Query.generated";
import { useRoutes } from "utils/routes";

import styles from "./style.module.scss";
import LazyLoader from "components/LazyLoader";
import { GridColumn, SortOrder } from "components/Grid/util";
import Grid from "components/Grid";
import { useQueryStringSearch } from "components/QueryStringSearch";
import { useQueryStringSort } from "utils/hooks";

type SchemaTable = SchemaTableListQuery["getSchemaTables"][number];

interface Props {
  databaseId: string;
  parentTableId?: string;
}

const Table: React.FunctionComponent<Props> = ({
  databaseId,
  parentTableId,
}) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const [includePartitions, setIncludePartitions] = useState(false);

  const secondaryTitle = (
    <>
      {!parentTableId && (
        <div className={styles.showIncludePartitions}>
          <input
            type="checkbox"
            checked={includePartitions}
            id="include_partitions"
            onChange={(evt) => setIncludePartitions(evt.target.checked)}
          />
          <label htmlFor="include_partitions">Show table partitions</label>
        </div>
      )}
      <SearchInput />
    </>
  );

  return (
    <Panel
      title={parentTableId ? "Table Partitions" : "All Tables"}
      secondaryTitle={secondaryTitle}
    >
      <TableContent
        databaseId={databaseId}
        parentTableId={parentTableId}
        searchTerm={searchTerm}
        includePartitions={includePartitions}
      />
    </Panel>
  );
};

const FETCH_BATCH_SIZE = 50;

const TableContent: React.FunctionComponent<{
  databaseId: string;
  parentTableId: string | undefined;
  searchTerm: string;
  includePartitions: boolean;
}> = ({ databaseId, parentTableId, searchTerm, includePartitions }) => {
  const [sortOpts, setSortOpts] = useQueryStringSort<
    keyof SchemaTable | "sizeBytes"
  >({
    param: "sort",
    sortBy: parentTableId ? "tableName" : "sizeBytes",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } = useSchemaTableListQuery({
    variables: {
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
      databaseId: databaseId,
      parentTableId: parentTableId,
      includePartitions: parentTableId ? null : includePartitions,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const schemaTables = data!.getSchemaTables;

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetched = fetchMoreResult.getSchemaTables;
          resolve(fetched.length > 0);
          if (!prev || !prev.getSchemaTables) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getSchemaTables: [...prev.getSchemaTables, ...fetched],
          };
        },
      });
    });
  };

  function handleSort(sortBy: keyof SchemaTable, sortOrder: SortOrder) {
    setSortOpts({
      sortBy,
      sortDirection: sortOrder,
    });
  }

  const gridData = data!.getSchemaTables.map((t) => {
    const { lastStats, stats7dAgo } = t;
    const {
      dataSizeBytes: sizeBytes,
      snapshotAt,
      liveTuples,
      deadTupleRatio,
      indexSizeBytes,
    } = lastStats ?? {};
    const { dataSizeBytes: sizeBytes7dAgo, snapshotAt: snapshotAt7dAgo } =
      stats7dAgo ?? {};
    const sizeBytesTrend =
      sizeBytes != null && sizeBytes7dAgo != null
        ? sizeBytes - sizeBytes7dAgo
        : undefined;

    return {
      ...t,
      liveTuples,
      deadTupleRatio,
      sizeBytes,
      snapshotAt,
      sizeBytes7dAgo,
      snapshotAt7dAgo,
      sizeBytesTrend,
      indexSizeBytes,
    };
  });

  const columns: GridColumn<
    (typeof gridData)[number],
    keyof (typeof gridData)[number]
  >[] = [
    {
      field: "schemaName",
      header: "Schema",
      width: parentTableId ? false : "min(10%,140px)",
    },
    {
      field: "tableName",
      header: "Name",
      width: parentTableId ? "2fr" : "1fr",
      nullValue: "n/a",
      renderer: function TableNameCell({ rowData }) {
        return (
          <NameAndIssuesCell databaseId={databaseId} schemaTable={rowData} />
        );
      },
    },
    {
      field: "partitionBoundary",
      header: "Partition Boundary",
      width: parentTableId ? "3fr" : false,
      style: "query",
    },
    {
      field: "liveTuples",
      header: "Est. Visible Rows",
      width: parentTableId ? "min(15%,140px)" : "15%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function LiveTuplesCell({ fieldData }) {
        return formatNumber(fieldData);
      },
    },
    {
      field: "deadTupleRatio",
      header: "Dead Row %",
      width: parentTableId ? "min(10%,120px)" : "10%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function DeadTupleRatioCell({ fieldData }) {
        return formatPercent(fieldData);
      },
    },
    {
      field: "sizeBytes",
      header: "Data Size",
      width: parentTableId ? "min(10%,120px)" : "12%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function SizeBytesCell({ fieldData }) {
        return formatBytes(fieldData);
      },
    },
    {
      field: "sizeBytesTrend",
      header: "7 Day Trend",
      width: parentTableId ? "min(20%,200px)" : "20%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function SizeBytesTrendCell({ rowData }) {
        const { sizeBytes, sizeBytes7dAgo, snapshotAt, snapshotAt7dAgo } =
          rowData;
        if (!sizeBytes || !sizeBytes7dAgo || !snapshotAt || !snapshotAt7dAgo) {
          return "n/a";
        }
        const content = formatBytesTrend(sizeBytes, sizeBytes7dAgo);
        const tooltip = formatBytesTrendDetail(
          sizeBytes,
          sizeBytes7dAgo,
          snapshotAt,
          snapshotAt7dAgo,
        );
        return <span title={tooltip}>{content}</span>;
      },
    },
    {
      field: "indexSizeBytes",
      header: "Index Size",
      width: parentTableId ? "min(10%,120px)" : "10%",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "n/a",
      renderer: function IndexSizeBytesCell({ fieldData }) {
        return formatBytes(fieldData);
      },
    },
  ];

  const sortedOrder = sortOpts.sortDirection;
  const sortedBy = sortOpts.sortBy as keyof SchemaTable;

  return (
    <LazyLoader loadedCount={schemaTables.length} loadMore={fetchMoreData}>
      <Grid
        striped
        data={gridData}
        columns={columns}
        sortedBy={sortedBy}
        sortedOrder={sortedOrder}
        noRowsText={
          parentTableId
            ? "No partitions or table inheritance children found"
            : "No tables found"
        }
        handleSort={handleSort}
      />
    </LazyLoader>
  );
};

const NameAndIssuesCell: React.FunctionComponent<{
  databaseId: string;
  schemaTable: SchemaTable;
}> = ({ databaseId, schemaTable }) => {
  const { databaseTable } = useRoutes();
  return (
    <span>
      <Link to={databaseTable(databaseId, schemaTable.id)}>
        {schemaTable.tableName}
      </Link>
      {schemaTable.issues.map((i) => (
        <Link
          to={databaseTable(databaseId, schemaTable.id)}
          className={`state-${i.severity}`}
          title={`${i.references[0].name} ${i.description}`}
          key={i.id}
        >
          <i className="icon-exclamation-sign" />
        </Link>
      ))}
    </span>
  );
};

export default Table;
